// src/managers/VideoSyncManager.js
import { useAppStore } from '../store/appStore';
import { useEffect } from 'react';

/**
 * VideoSyncManager handles synchronization between all video components
 * It acts as the central authority for video playback state
 */
class VideoSyncManager {
  constructor() {
    this.videoRefs = new Map(); // Store all video references
    this.processingQueue = false; // Flag to prevent operation overlap
    this.lastUpdateTime = Date.now(); // Throttle updates
    this.updateThreshold = 20; // Reduced from 50ms to 20ms
    this.initialLoad = true; // Flag for initial load
    this.currentRobotId = null; // Track current robot ID
    this.isVideoChanging = false; // Flag for video source change
    this.loadTimeout = 300; // Reduced from 500ms to 300ms
    this.lastKnownTime = 0; // Track last known time for reference
    this.isSyncingTime = false; // Flag to prevent time sync loops
    this.syncInProgress = false; // Flag to track active sync operations
    
    // Track the last video switch we processed to avoid duplicate processing
    this.lastProcessedVideoSwitch = null;
    
    // 添加锁定开始时间跟踪
    this.videoChangeStartTime = null;
    this.syncStartTime = null;
    this.lockTimeouts = {};
  }
  
  /**
   * 安全锁设置函数 - 添加超时保护
   * @param {string} lockName - 锁的名称
   * @param {boolean} value - 锁的状态
   * @param {number} timeout - 安全超时时间
   */
  setLock(lockName, value, timeout = 5000) {
    const timestamp = new Date().toISOString();
    
    if (value === true) {
      console.log(`[VideoSync ${timestamp}] 🔒 设置锁: ${lockName}`);
      this[lockName] = true;
      this[`${lockName}StartTime`] = Date.now();
      
      // 清除之前的超时
      if (this.lockTimeouts[lockName]) {
        clearTimeout(this.lockTimeouts[lockName]);
      }
      
      // 设置安全超时
      this.lockTimeouts[lockName] = setTimeout(() => {
        if (this[lockName] === true) {
          console.warn(`[VideoSync ${timestamp}] 🔓 安全超时触发: 强制释放锁 ${lockName}, 持续时间: ${(Date.now() - this[`${lockName}StartTime`])/1000}秒`);
          this[lockName] = false;
          this[`${lockName}StartTime`] = null;
        }
      }, timeout);
    } else {
      console.log(`[VideoSync ${timestamp}] 🔓 释放锁: ${lockName}`);
      this[lockName] = false;
      this[`${lockName}StartTime`] = null;
      
      // 清除超时
      if (this.lockTimeouts[lockName]) {
        clearTimeout(this.lockTimeouts[lockName]);
        this.lockTimeouts[lockName] = null;
      }
    }
  }
  
  /**
   * 检查锁状态和自动释放卡住的锁
   * @param {string} lockName - 锁的名称
   * @returns {boolean} - 锁的当前状态
   */
  checkLock(lockName) {
    const timestamp = new Date().toISOString();
    
    // 如果锁已持续超过5秒，强制释放
    if (this[lockName] && this[`${lockName}StartTime`] && 
        (Date.now() - this[`${lockName}StartTime`] > 5000)) {
      console.warn(`[VideoSync ${timestamp}] ⚠️ 检测到卡住的锁 ${lockName}，已持续${(Date.now() - this[`${lockName}StartTime`])/1000}秒，强制释放`);
      this.setLock(lockName, false);
      return false;
    }
    return this[lockName];
  }
  
  /**
   * Register a video component
   * @param {string} id - Component identifier
   * @param {React.RefObject} ref - Reference to the video component
   * @returns {Function} - Unregister function
   */
  registerVideo(id, ref) {
    const timestamp = new Date().toISOString();
    console.log(`[VideoSync ${timestamp}] 📝 注册视频 ${id}`);
    this.videoRefs.set(id, ref);
    
    // Return unregister function
    return () => {
      const timestamp = new Date().toISOString();
      console.log(`[VideoSync ${timestamp}] 🗑️ 注销视频 ${id}`);
      this.videoRefs.delete(id);
    };
  }
  
  /**
   * Play all registered videos
   * @param {string} sourceId - Identifier of the triggering component
   */
  playAll(sourceId = null) {
    const timestamp = new Date().toISOString();
    
    // 检查锁状态，如果有卡住的锁，强制释放
    if (this.checkLock('isVideoChanging') || this.checkLock('syncInProgress')) {
      console.log(`[VideoSync ${timestamp}] ⏭️ 视频变化或同步正在进行中，跳过播放请求`);
      return;
    }
    
    console.log(`[VideoSync ${timestamp}] ▶️ 播放所有视频，请求来源: ${sourceId || '未知'}`);
    
    // Update global state through Zustand
    const store = useAppStore.getState();
    
    // FIX: 确保清除 lastVideoSwitch 标志
    if (store.videoState.lastVideoSwitch) {
      store.updateVideoState({ 
        isPlaying: true,
        lastVideoSwitch: null  // 主动清除标志
      }, sourceId || 'syncManager');
    } else {
      store.updateVideoState({ isPlaying: true }, sourceId || 'syncManager');
    }
    
    // Play all videos
    let playPromises = [];
    this.videoRefs.forEach((ref, id) => {
      if (id === sourceId) return; // Skip source to prevent feedback loops
      if (!ref || !ref.current) return;
      
      try {
        if (ref.current.paused) {
          const promise = ref.current.play().catch(err => {
            console.warn(`[VideoSync ${timestamp}] ⚠️ 播放视频 ${id} 失败:`, err);
          });
          playPromises.push(promise);
        }
      } catch (err) {
        console.warn(`[VideoSync ${timestamp}] ⚠️ 播放视频 ${id} 出现异常:`, err);
      }
    });
    
    // 等待所有播放请求完成
    Promise.all(playPromises).then(() => {
      console.log(`[VideoSync ${timestamp}] ✅ 所有播放请求已处理`);
    }).catch(err => {
      console.warn(`[VideoSync ${timestamp}] ⚠️ 播放请求过程中出错:`, err);
    });
  }
  
  /**
   * Pause all registered videos
   * @param {string} sourceId - Identifier of the triggering component
   */
  pauseAll(sourceId = null) {
    const timestamp = new Date().toISOString();
    console.log(`[VideoSync ${timestamp}] ⏸️ 暂停所有视频，请求来源: ${sourceId || '未知'}`);
    
    // Update global state through Zustand
    const store = useAppStore.getState();
    
    // FIX: 同样在暂停时确保清除 lastVideoSwitch 标志
    if (store.videoState.lastVideoSwitch) {
      store.updateVideoState({ 
        isPlaying: false,
        lastVideoSwitch: null  // 主动清除标志
      }, sourceId || 'syncManager');
    } else {
      store.updateVideoState({ isPlaying: false }, sourceId || 'syncManager');
    }
    
    // Pause all videos
    this.videoRefs.forEach((ref, id) => {
      if (id === sourceId) return; // Skip source to prevent feedback loops
      if (!ref || !ref.current) return;
      
      try {
        if (!ref.current.paused) {
          ref.current.pause();
        }
      } catch (err) {
        console.warn(`[VideoSync ${timestamp}] ⚠️ 暂停视频 ${id} 出现异常:`, err);
      }
    });
  }
  
  /**
   * Seek all registered videos to a specific time
   * @param {number} time - Target time in seconds
   * @param {string} sourceId - Identifier of the triggering component
   */
  seekAll(time, sourceId = null) {
    const timestamp = new Date().toISOString();
    
    // 检查锁状态，防止冲突
    if (this.checkLock('syncInProgress') || this.checkLock('isSyncingTime')) {
      console.log(`[VideoSync ${timestamp}] ⏭️ 同步正在进行中，跳过寻址请求`);
      return;
    }
    
    // Throttle updates to prevent overwhelming the system
    const now = Date.now();
    if (now - this.lastUpdateTime < this.updateThreshold) {
      return;
    }
    this.lastUpdateTime = now;
    
    // Skip if time is close to last known time (within 0.2s)
    if (Math.abs(time - this.lastKnownTime) < 0.2) {
      return;
    }
    
    console.log(`[VideoSync ${timestamp}] ⏩ 将所有视频跳转到 ${time}秒，请求来源: ${sourceId || '未知'}`);
    
    // 设置同步锁，带3秒安全超时
    this.setLock('isSyncingTime', true, 3000);
    this.setLock('syncInProgress', true, 3000);
    this.lastKnownTime = time;
    
    // 优化：使用更智能的寻址策略
    // 对于小的时间变化，避免暂停/播放循环
    const isSmallTimeChange = this.isVideoChanging === false && 
                              this.videoRefs.size > 0 && 
                              Math.abs(time - this.lastKnownTime) < 2.0;
    
    // 如果不是小的时间变化，暂停所有视频
    if (!isSmallTimeChange) {
      console.log(`[VideoSync ${timestamp}] ⏸️ 暂停所有视频准备大范围寻址`);
      this.pauseAll(sourceId);
    }
    
    // Update global state through Zustand
    const store = useAppStore.getState();
    
    // FIX: 在时间更新时一并清除 lastVideoSwitch 标志
    store.updateVideoState({ 
      time: time,
      isPlaying: isSmallTimeChange, // 小的时间变化保持播放状态
      lastVideoSwitch: null  // 确保清除此标志
    }, sourceId || 'syncManager');
    
    // Apply time to all videos
    const seekPromises = [];
    this.videoRefs.forEach((ref, id) => {
      if (!ref || !ref.current) return;
      
      try {
        // 只在时间差异显著时更新
        if (Math.abs(ref.current.currentTime - time) > 0.5) {
          ref.current.currentTime = time;
          seekPromises.push(new Promise(resolve => {
            // 使用更短的等待时间
            setTimeout(resolve, 30); // 从50ms减少到30ms
          }));
        }
      } catch (err) {
        console.warn(`[VideoSync ${timestamp}] ⚠️ 寻址视频 ${id} 出现异常:`, err);
      }
    });
    
    // Resume playback with minimal delay
    Promise.all(seekPromises).then(() => {
      setTimeout(() => {
        // 释放锁
        this.setLock('isSyncingTime', false);
        this.setLock('syncInProgress', false);
        
        // 如果不是小的时间变化，恢复播放
        if (!isSmallTimeChange) {
          console.log(`[VideoSync ${timestamp}] ▶️ 寻址完成，恢复播放`);
          this.playAll(sourceId);
        } else {
          console.log(`[VideoSync ${timestamp}] ✅ 小范围寻址完成，无需重新启动播放`);
        }
      }, 30); // 从50ms减少到30ms
    }).catch(err => {
      console.warn(`[VideoSync ${timestamp}] ⚠️ 寻址过程中出错:`, err);
      // 确保锁被释放
      this.setLock('isSyncingTime', false);
      this.setLock('syncInProgress', false);
    });
  }

  /**
   * Handle video source change - 修复版本
   * @param {string} videoUrl - New video URL
   * @param {number} robotId - Robot ID associated with the video
   * @param {number} time - Time to seek to after changing
   * @param {string} sourceId - Identifier of the triggering component
   */
  handleVideoChange(videoUrl, robotId, time, sourceId = null) {
    const timestamp = new Date().toISOString();
    
    // 检查是否有进行中的视频变化
    if (this.checkLock('isVideoChanging')) {
      console.log(`[VideoSync ${timestamp}] ⏭️ 已有视频变化正在处理，排队请求`);
      setTimeout(() => {
        this.handleVideoChange(videoUrl, robotId, time, sourceId);
      }, 100);
      return;
    }
    
    // 获取当前状态，以获取准确的时间值
    const store = useAppStore.getState();
    
    // 确定要保留的时间
    // 如果提供了时间参数，则使用它
    // 否则使用当前全局时间
    const timeToPreserve = (time !== undefined && time !== null && !isNaN(time)) 
      ? time 
      : store.videoState.currentTime;
    
    // 设置视频变化锁
    this.setLock('isVideoChanging', true, 4000);
    this.pauseAll(sourceId);
    
    console.log(`[VideoSync ${timestamp}] 🔄 更改视频到 ${videoUrl}, 机器人: ${robotId}, 保留时间: ${timeToPreserve}`);
    
    // 检查是否是HLS视频
    const isHls = videoUrl.includes('.m3u8');
    
    // 第一步更新：机器人ID和视频URL，但保留时间
    store.updateVideoState({ 
      robotId,
      videoUrl,
      isPlaying: false,
      lastVideoSwitch: Date.now(),
      time: timeToPreserve, // 显式保留时间
      isHls: isHls,
      hlsBaseTime: isHls ? timeToPreserve : 0 // 如果是HLS视频，设置基准时间
    }, sourceId || 'syncManager');
    
    this.lastKnownTime = timeToPreserve;
    
    // 使用更短的等待时间
    setTimeout(() => {
      console.log(`[VideoSync ${timestamp}] ✅ 视频加载超时结束，准备恢复播放，时间: ${timeToPreserve}`);
      
      // Clear video change flag
      this.setLock('isVideoChanging', false);
      
      // 在一个单独的更新中再次包含时间，确保它不会丢失
      setTimeout(() => {
        if (!this.syncInProgress && !this.isSyncingTime) {
          console.log(`[VideoSync ${timestamp}] ▶️ 视频变化后恢复播放，时间: ${timeToPreserve}`);
          store.updateVideoState({ 
            isPlaying: true,
            lastVideoSwitch: null,
            time: timeToPreserve // 再次确认时间保留
          }, sourceId || 'syncManager');
          
          this.playAll(sourceId);
        }
      }, 30);
    }, this.loadTimeout);
  }
  
  /**
   * Set the load timeout for faster video switching
   * @param {number} timeoutMs - New timeout in milliseconds
   */
  setLoadTimeout(timeoutMs) {
    if (typeof timeoutMs === 'number' && timeoutMs > 0) {
      this.loadTimeout = timeoutMs;
      console.log(`[VideoSync] 设置视频加载超时为 ${timeoutMs}ms`);
    }
  }
  
  /**
   * 优化的事件点击处理函数
   * @param {number} startTime - 事件开始时间
   * @param {number} robotId - 事件相关的机器人ID
   * @param {number} eventId - 事件ID
   */
  handleEventClick(startTime, robotId, eventId) {
    const timestamp = new Date().toISOString();
    console.log(`[VideoSync ${timestamp}] 📍 事件点击: time=${startTime}, robot=${robotId}, event=${eventId}`);
    
    // 检查锁状态
    if (this.checkLock('isVideoChanging')) {
      console.log(`[VideoSync ${timestamp}] ⏭️ 已有视频变化正在进行，跳过请求`);
      return;
    }
    
    // 设置锁，带3秒安全超时
    this.setLock('isVideoChanging', true, 3000);
    
    // 获取当前状态
    const store = useAppStore.getState();
    const timeframe = store.timeframe;
    const currentVideoUrl = store.videoUrl;
    const currentRobotId = store.selection.activeRobotId;
    
    // 构建视频URL
    const timePart = timeframe === "11AM" ? "day" : "night";
    
    // 检查是否有HLS视频可用
    const eventKey = startTime.toFixed(1);
    const robotKey = `robot${robotId}`;
    const hlsInfo = window.eventIndex?.[timePart]?.[robotKey]?.[eventKey];
    
    let videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
    let isHls = false;
    let hlsResumeTime = null;
    let hlsBaseTime = 0;
    
    // 如果找到HLS视频信息
    if (hlsInfo && hlsInfo.m3u8) {
      console.log(`[VideoSync ${timestamp}] 🎯 检测到HLS视频: ${hlsInfo.m3u8}`);
      videoUrl = hlsInfo.m3u8;
      isHls = true;
      
      // 设置HLS相关属性
      hlsResumeTime = hlsInfo.end_time || (startTime + 60); // 使用结束时间或默认60秒
      hlsBaseTime = startTime;
      
      console.log(`[VideoSync ${timestamp}] ⏱️ HLS视频信息: baseTime=${hlsBaseTime}, resumeTime=${hlsResumeTime}`);
    }
    
    // 检查是否需要切换视频源
    const isVideoSwitch = videoUrl !== currentVideoUrl || robotId !== currentRobotId;
    
    console.log(`[VideoSync ${timestamp}] ${isVideoSwitch ? '🔄' : '⏩'} ${isVideoSwitch ? '需要' : '不需要'}切换视频源`);
    
    // 优化：针对不同情况采用不同策略
    if (isVideoSwitch) {
      // 视频源变化：暂时暂停
      console.log(`[VideoSync ${timestamp}] ⏸️ 暂停播放准备切换视频源`);
      store.updateVideoState({
        robotId,
        eventId,
        videoUrl,
        time: startTime,
        isPlaying: false, // 暂停
        isHls: isHls,
        hlsResumeTime: hlsResumeTime,
        hlsBaseTime: hlsBaseTime
      }, 'timeline_event_click');
      
      // 减少延迟时间
      console.log(`[VideoSync ${timestamp}] ⏱️ 设置50ms后恢复播放`);
      setTimeout(() => {
        // 恢复播放
        console.log(`[VideoSync ${timestamp}] ▶️ 恢复播放`);
        store.updateVideoState({ 
          isPlaying: true,
          lastVideoSwitch: null  // 确保清除此标志
        }, 'timeline_event_click');
        
        // 快速释放锁
        setTimeout(() => {
          this.setLock('isVideoChanging', false);
        }, 20);
      }, 50); // 从100ms减少到50ms
    } else {
      // 同视频内时间跳转：无需暂停
      console.log(`[VideoSync ${timestamp}] ⏩ 同视频内时间跳转，无需暂停播放`);
      store.updateVideoState({
        robotId,
        eventId, 
        time: startTime,
        isPlaying: true, // 保持播放
        lastVideoSwitch: null,  // 确保清除此标志
        isHls: isHls,
        hlsResumeTime: hlsResumeTime,
        hlsBaseTime: hlsBaseTime
      }, 'timeline_event_click');
      
      // 立即释放锁
      this.setLock('isVideoChanging', false);
    }

    // 更新最后时间
    this.lastKnownTime = startTime;
  }
  
  /**
   * 同步与全局状态
   * 根据Zustand全局状态更新本地状态
   * 该方法已修改以防止时间同步循环
   */
  syncWithGlobalState() {
    const timestamp = new Date().toISOString();
    
    // 检查锁状态
    if (this.checkLock('syncInProgress') || this.checkLock('isSyncingTime')) {
      return;
    }
    
    // 获取Zustand状态
    const { 
      videoState: { isPlaying, currentTime, lastVideoSwitch },
      selection: { activeRobotId },
      videoUrl
    } = useAppStore.getState();
    
    // 检查视频是否正在变化
    if (lastVideoSwitch && !this.isVideoChanging) {
      // FIX: 避免重复处理相同的 lastVideoSwitch
      if (lastVideoSwitch === this.lastProcessedVideoSwitch) {
        console.log(`[VideoSync ${timestamp}] ⏭️ 跳过已处理的视频切换标志`);
        
        // 自动清除持久的标志
        const store = useAppStore.getState();
        store.updateVideoState({ lastVideoSwitch: null }, 'videoSyncCleanup');
        return;
      }
      
      // 记录处理过的标志时间戳
      this.lastProcessedVideoSwitch = lastVideoSwitch;
      
      console.log(`[VideoSync ${timestamp}] 🔄 检测到全局视频切换标志`);
      this.setLock('isVideoChanging', true, 3000);
      
      // 等待变化完成 - 使用更短的超时
      setTimeout(() => {
        this.setLock('isVideoChanging', false);
        
        // 主动清除持久标志
        const store = useAppStore.getState();
        store.updateVideoState({ lastVideoSwitch: null }, 'videoSyncCleanup');
      }, 150); // 从300ms减少到150ms
      
      return;
    }
    
    // 视频变化中跳过
    if (this.checkLock('isVideoChanging')) {
      return;
    }
    
    // 更新机器人ID
    if (activeRobotId !== this.currentRobotId) {
      this.currentRobotId = activeRobotId;
    }
    
    // 重要改进：只同步播放状态，不同步时间
    // 这防止了4秒循环问题
    if (isPlaying) {
      this.videoRefs.forEach((ref, id) => {
        if (!ref || !ref.current) return;
        
        try {
          if (ref.current.paused) {
            ref.current.play().catch(err => {
              console.warn(`[VideoSync ${timestamp}] ⚠️ 播放视频 ${id} 失败:`, err);
            });
          }
        } catch (err) {
          console.warn(`[VideoSync ${timestamp}] ⚠️ 播放视频 ${id} 出现异常:`, err);
        }
      });
    } else {
      this.videoRefs.forEach((ref, id) => {
        if (!ref || !ref.current) return;
        
        try {
          if (!ref.current.paused) {
            ref.current.pause();
          }
        } catch (err) {
          console.warn(`[VideoSync ${timestamp}] ⚠️ 暂停视频 ${id} 出现异常:`, err);
        }
      });
    }
  }
  
  /**
   * 订阅存储变化
   * 设置Zustand状态变化的监听器
   * @returns {Function} 取消订阅函数
   */
  subscribeToStore() {
    return useAppStore.subscribe(
      (state) => [
        state.videoState.isPlaying, 
        state.videoState.currentTime, 
        state.selection.activeRobotId,
        state.videoUrl,
        state.videoState.lastVideoSwitch
      ],
      ([isPlaying, currentTime, activeRobotId, videoUrl, lastVideoSwitch], 
       [prevIsPlaying, prevCurrentTime, prevActiveRobotId, prevVideoUrl, prevLastVideoSwitch]) => {
        const timestamp = new Date().toISOString();
        
        // 跳过已在同步中的情况
        if (this.syncInProgress || this.isSyncingTime) return;
        
        // 检测视频源变化
        if (videoUrl !== prevVideoUrl || activeRobotId !== prevActiveRobotId) {
          console.log(`[VideoSync ${timestamp}] 🔄 从存储中检测到视频变化: ${prevVideoUrl} -> ${videoUrl}`);
          this.setLock('isVideoChanging', true, 3000);
          
          // 使用更短的延迟重置
          setTimeout(() => {
            this.setLock('isVideoChanging', false);
          }, 150); // 从300ms减少到150ms
          return;
        }
        
        // 检测视频切换标志
        if (lastVideoSwitch !== prevLastVideoSwitch && lastVideoSwitch) {
          // FIX: 避免重复处理相同的 lastVideoSwitch
          if (lastVideoSwitch === this.lastProcessedVideoSwitch) {
            console.log(`[VideoSync ${timestamp}] ⏭️ 跳过已处理的视频切换标志`);
            
            // 自动清除持久的标志
            const store = useAppStore.getState();
            store.updateVideoState({ lastVideoSwitch: null }, 'videoSyncCleanup');
            return;
          }
          
          // 记录我们处理的标志
          this.lastProcessedVideoSwitch = lastVideoSwitch;
          
          console.log(`[VideoSync ${timestamp}] 🔄 从存储中检测到视频切换标志`);
          this.setLock('isVideoChanging', true, 3000);
          
          // 立即清除全局视频切换标志
          const store = useAppStore.getState();
          console.log(`[VideoSync ${timestamp}] 🧹 立即清除全局视频切换标志`);
          store.updateVideoState({
            lastVideoSwitch: null
          }, 'videosync_cleanup');
          
          // 使用更短的延迟重置锁定
          setTimeout(() => {
            this.setLock('isVideoChanging', false);
          }, 150);
          return;
        }
        
        // 视频变化中跳过同步
        if (this.checkLock('isVideoChanging')) {
          return;
        }
        
        // 处理播放状态变化
        if (isPlaying !== prevIsPlaying) {
          if (isPlaying) {
            console.log(`[VideoSync ${timestamp}] ▶️ 从存储中收到播放命令`);
            this.playAll('store');
          } else {
            console.log(`[VideoSync ${timestamp}] ⏸️ 从存储中收到暂停命令`);
            this.pauseAll('store');
          }
        }
        
        // 重要改进：仅处理显著的时间变化(> 1.5秒)
        // 这可以防止4秒循环问题
        if (Math.abs(currentTime - prevCurrentTime) > 1.5) {
          console.log(`[VideoSync ${timestamp}] ⏩ 从存储中检测到显著时间变化: ${prevCurrentTime} -> ${currentTime}`);
          this.seekAll(currentTime, 'store');
        }
      }
    );
  }
  
  /**
   * 调试函数：打印所有锁的状态
   */
  debugLocks() {
    const timestamp = new Date().toISOString();
    const locks = {
      isVideoChanging: {
        state: this.isVideoChanging,
        since: this.isVideoChanging ? (Date.now() - this.videoChangeStartTime) : null
      },
      syncInProgress: {
        state: this.syncInProgress,
        since: this.syncInProgress ? (Date.now() - this.syncStartTime) : null
      },
      isSyncingTime: {
        state: this.isSyncingTime,
        since: this.isSyncingTime ? (Date.now() - this.syncStartTime) : null
      }
    };
    
    console.log(`[VideoSync ${timestamp}] 🔍 锁状态:`, locks);
    
    // 检查并释放卡住的锁
    Object.keys(locks).forEach(lockName => {
      this.checkLock(lockName);
    });
    
    return locks;
  }
  
  /**
   * 主动清除视频切换标志
   * 该方法可以在合适的时候主动清除持久化的视频切换标志
   */
  clearVideoSwitchFlag() {
    const timestamp = new Date().toISOString();
    const store = useAppStore.getState();
    
    if (store.videoState.lastVideoSwitch) {
      console.log(`[VideoSync ${timestamp}] 🧹 主动清除全局视频切换标志`);
      store.updateVideoState({
        lastVideoSwitch: null
      }, 'videosync_cleanup');
      return true;
    }
    return false;
  }
}

// Export singleton instance
const instance = new VideoSyncManager();
export default instance;